/**
 * Loading Spinner Component
 * @author Muhammad Arsan
 * @description A reusable Button component,  component using Material-UI's Button component
 */

import React from "react";
import CircularProgress from "@mui/material/CircularProgress";
import type { ButtonProps, ButtonVariant } from "./types/button";

const variantClasses: Record<ButtonVariant, string> = {
	primary: "bg-accent text-white hover:bg-accent-hover",
	secondary: "bg-accent-40 text-accent hover:bg-gray-200",
	icon: "text-black hover:text-accent p-1",
	tertiary: "bg-white font-semibold text-accent border border-accent hover:bg-accent-40",
	danger: "bg-danger text-white hover:bg-orange-700",
	dangerSecondary: "bg-orange-500 text-white hover:bg-orange-600 font-semibold",
	dangerTertiary: "bg-white font-semibold text-orange-500 border border-orange-500 hover:bg-orange-50",
};

const authSizeClasses =
	"h-[45px] min-h-[45px] max-h-[45px] box-border py-0 px-[14px] rounded-[10px] font-sans text-base font-semibold leading-6 transition-colors duration-150 flex items-center justify-center gap-2";

const Button: React.FC<ButtonProps> = React.memo(
	({
		text,
		variant = "primary",
		size = "default",
		loading = false,
		isDisabled = false,
		className = "",
		disabled,
		children,
		type = "button",
		...props
	}) => {
		const isButtonDisabled = loading || isDisabled || disabled;

		const baseClasses =
			variant === "icon"
				? "font-sans tracking-wide transition-colors duration-150 flex items-center justify-center"
				: size === "auth"
					? authSizeClasses
					: "min-h-10 rounded-lg font-sans text-sm tracking-wide px-6 py-2 transition-colors duration-150 flex items-center justify-center gap-2";

		const hoverClasses = variant === "icon" || size === "auth" ? "" : "hover:shadow-md";
		return (
			<button
				type={type}
				className={`${baseClasses} ${variantClasses[variant]} ${
					isButtonDisabled ? "opacity-60 cursor-not-allowed" : hoverClasses
				} ${className}`}
				disabled={isButtonDisabled}
				{...props}>
				{loading && <CircularProgress size={18} color="inherit" />}
				{children ? children : text}
			</button>
		);
	},
);

export default Button;
